import { numberToCurrency } from '@/utils'
import { moneyMovementCount } from '../model/activityView'
import { consecutiveRuns } from '../model/months'
import type { NotCounted } from '../model/portfolioOverview'
import type { ActivityTotals, MonthKey } from '../model/types'
import {
  countOf,
  joinWithAnd,
  monthSpanText,
  otherActivitySentence,
} from '../portfolioText'

export interface MoneyRow {
  label: string
  // Null reads "None found".
  amount: number | null
  detail?: string
  isTotal?: boolean
}

const listOf = (parts: (string | false)[]) =>
  parts.filter((part): part is string => Boolean(part)).join(', ')

const depositDetail = ({ deposits }: ActivityTotals) =>
  listOf([
    deposits.depositCount > 0 && countOf(deposits.depositCount, 'deposit'),
    deposits.contributionCount > 0 &&
      countOf(deposits.contributionCount, 'contribution'),
  ])

const withdrawalDetail = ({ withdrawals }: ActivityTotals) =>
  listOf([
    withdrawals.withdrawalCount > 0 &&
      countOf(withdrawals.withdrawalCount, 'withdrawal'),
    withdrawals.distributionCount > 0 &&
      countOf(withdrawals.distributionCount, 'distribution'),
  ])

const figureRow = (
  label: string,
  figure: { amount: number; count: number },
  detail?: string,
): MoneyRow =>
  figure.count === 0
    ? { label, amount: null }
    : { label, amount: figure.amount, detail: detail || undefined }

const interestDetail = ({ interest }: ActivityTotals) =>
  interest.charged === 0
    ? countOf(interest.count, 'payment')
    : `${numberToCurrency(interest.earned)} earned, ${numberToCurrency(Math.abs(interest.charged))} charged`

const hasMoneyMovements = (totals: ActivityTotals) =>
  moneyMovementCount(totals) + totals.fees.count > 0

const incomeRows = (totals: ActivityTotals): MoneyRow[] => [
  figureRow(
    'Dividends',
    totals.dividends,
    countOf(totals.dividends.count, 'payment'),
  ),
  figureRow('Interest', totals.interest, interestDetail(totals)),
]

const commissionRows = (totals: ActivityTotals): MoneyRow[] =>
  totals.commissions === 0
    ? []
    : [
        {
          label: 'Commissions',
          amount: -totals.commissions,
          detail: 'On trade rows, inside the trade amounts',
        },
      ]

export interface MoneyColumn {
  // Said in place of the money rows when both sources have none.
  noneSentence: string | null
  movements: MoneyRow[]
  income: MoneyRow[]
}

export const NO_MONEY_SENTENCE =
  'No deposits, withdrawals, transfers or fees found in either.'

const unsortedRows = ({
  unsortedIn,
  unsortedOut,
}: ActivityTotals): MoneyRow[] =>
  unsortedIn.count + unsortedOut.count === 0
    ? []
    : [
        figureRow(
          'Unsorted money in',
          unsortedIn,
          countOf(unsortedIn.count, 'ledger row'),
        ),
        figureRow(
          'Unsorted money out',
          unsortedOut,
          countOf(unsortedOut.count, 'ledger row'),
        ),
      ]

export const moneyColumn = (totals: ActivityTotals): MoneyColumn => {
  if (!hasMoneyMovements(totals)) {
    return {
      noneSentence:
        totals.other.count > 0
          ? otherActivitySentence(totals.other.count)
          : NO_MONEY_SENTENCE,
      movements: [],
      income: [...incomeRows(totals), ...commissionRows(totals)].filter(
        (row) => row.amount != null,
      ),
    }
  }
  return {
    noneSentence: null,
    movements: [
      figureRow('Deposits', totals.deposits, depositDetail(totals)),
      figureRow('Withdrawals', totals.withdrawals, withdrawalDetail(totals)),
      figureRow(
        'Transfers in',
        totals.transfersIn,
        countOf(totals.transfersIn.count, 'transfer'),
      ),
      figureRow(
        'Transfers out',
        totals.transfersOut,
        countOf(totals.transfersOut.count, 'transfer'),
      ),
      ...unsortedRows(totals),
      { label: 'Net money in and out', amount: totals.netMoney, isTotal: true },
    ],
    income: [
      ...incomeRows(totals),
      ...commissionRows(totals),
      figureRow('Fees', totals.fees, countOf(totals.fees.count, 'fee row')),
    ],
  }
}

// What the money figures leave out, said once under the column label.
export const leftOutSentence = (
  totals: ActivityTotals,
  accountCount: number,
): string => {
  if (accountCount === 1) {
    return 'From the activity and the cash ledger. Sweeps inside the account are left out.'
  }
  const paired = totals.leftOut.pairedTransfers
  const pairedPart =
    paired === 0
      ? ''
      : `, and ${countOf(paired, 'transfer')} paired between these accounts,`
  return `From each account's activity and its cash ledger. Sweeps${pairedPart} are left out.`
}

const REASON_WORDS: Record<NotCounted['reason'], string> = {
  no_statement: 'no statement',
  verifying: 'still being verified',
  failed_read: 'could not be read',
}

const runsText = (months: MonthKey[]) =>
  joinWithAnd(
    consecutiveRuns(months).map((run) =>
      monthSpanText(run[0], run[run.length - 1]),
    ),
  )

const groupBy = <Item>(items: Item[], keyOf: (item: Item) => string) => {
  const groups = new Map<string, Item[]>()
  for (const item of items) {
    const key = keyOf(item)
    groups.set(key, [...(groups.get(key) ?? []), item])
  }
  return groups
}

interface AccountGap {
  accountName: string
  reason: NotCounted['reason']
  months: string
}

const accountGaps = (notCounted: NotCounted[]): AccountGap[] =>
  Array.from(
    groupBy(notCounted, (entry) => `${entry.reason}|${entry.accountName}`),
  ).map(([, entries]) => ({
    accountName: entries[0].accountName,
    reason: entries[0].reason,
    months: runsText(entries.map((entry) => entry.month)),
  }))

// "Not counted: Nov 2022 for Charles Schwab •• 8817 (no statement)."
export const notCountedSentence = (notCounted: NotCounted[]): string | null => {
  if (notCounted.length === 0) return null
  const gaps = accountGaps(notCounted)
  const reasons = new Set(gaps.map((gap) => gap.reason))
  const parts = Array.from(
    groupBy(gaps, (gap) => `${gap.reason}|${gap.months}`),
  ).map(([, sameGaps]) => {
    const names = joinWithAnd(sameGaps.map((gap) => gap.accountName))
    const part = `${sameGaps[0].months} for ${names}`
    return reasons.size > 1
      ? `${part} (${REASON_WORDS[sameGaps[0].reason]})`
      : part
  })
  const ending = reasons.size > 1 ? '' : ` (${REASON_WORDS[gaps[0].reason]})`
  return `Not counted: ${joinWithAnd(parts)}${ending}.`
}
